import { useRef, useCallback, type ReactNode, type CSSProperties } from "react";
import { cn } from "@/lib/utils";

interface SpotlightCardProps {
  children: ReactNode;
  className?: string;
}

/**
 * Card com efeito de Spotlight: um brilho dourado (tokens da marca)
 * que segue o cursor no hover. CSS puro via variáveis — sem custo de JS por frame.
 */
const SpotlightCard = ({ children, className }: SpotlightCardProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
    el.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
  }, []);

  return (
    <div
      ref={ref}
      onMouseMove={handleMouseMove}
      style={{ "--mouse-x": "50%", "--mouse-y": "50%" } as CSSProperties}
      className={cn(
        "group/spotlight relative overflow-hidden rounded-2xl border-2 border-border bg-card transition-colors duration-500 hover:border-primary/40",
        // Camada de brilho que acompanha o mouse (desativada em touch/reduced-motion)
        "before:pointer-events-none before:absolute before:inset-0 before:z-0 before:opacity-0 before:transition-opacity before:duration-500 before:group-hover/spotlight:opacity-100",
        "before:bg-[radial-gradient(500px_circle_at_var(--mouse-x)_var(--mouse-y),hsl(var(--ring)/0.14),transparent_65%)]",
        "motion-reduce:before:hidden",
        className
      )}
    >
      {/* Borda iluminada perto do cursor */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 z-0 rounded-2xl opacity-0 transition-opacity duration-500 group-hover/spotlight:opacity-100 motion-reduce:hidden"
        style={{
          background:
            "radial-gradient(320px circle at var(--mouse-x) var(--mouse-y), hsl(var(--ring) / 0.35), transparent 70%)",
          WebkitMask:
            "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          padding: "2px",
        }}
      />
      <div className="relative z-10 h-full">{children}</div>
    </div>
  );
};

export default SpotlightCard;
